import { useEffect, useState } from "react";
import { Divider, Skeleton } from "@mui/material";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from "../../../../components";
import { OrdemServicoService } from "../../../../services";
import { responseErros } from "../../../../utils";

const ordemServicoService = new OrdemServicoService();

const COR_ATRASO = "#d32f2f";
const COR_TEMPO = "#1976d2";

function formatarHoras(horas) {
  if (!horas || horas <= 0) return "0h";
  if (horas < 24) return `${Math.round(horas)}h`;
  if (horas < 720) {
    const dias = horas / 24;
    return `${Number(dias.toFixed(1))} ${dias >= 2 ? "dias" : "dia"}`;
  }
  const meses = horas / 720;
  return `${Number(meses.toFixed(1))} ${meses >= 2 ? "meses" : "mês"}`;
}

function ListaBarras({ titulo, itens, cor, max, formatarValor, renderSub }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {titulo}
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >
        {itens.map((item) => {
          const pct = max > 0 ? (item.valor / max) * 100 : 0;
          return (
            <Box key={item.statusId}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={0.5}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      backgroundColor: item.cor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1e293b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={item.status}
                  >
                    {item.status}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right", flexShrink: 0, pl: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      pt: !renderSub ? "13px" : 0,
                    }}
                  >
                    {formatarValor(item)}
                  </Typography>
                  {renderSub && renderSub(item)}
                </Box>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#f1f5f9",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${pct}%`,
                    height: "100%",
                    borderRadius: 4,
                    backgroundColor: cor,
                    transition: "width 0.3s",
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function ResumoTotais({ totalAtrasadas, gargalo }) {
  return (
    <Box
      sx={{
        mt: "auto",
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "#f8fafc",
        border: "1px solid #eef2f7",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Total de O.S atrasadas
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: COR_ATRASO }}>
          {totalAtrasadas}
        </Typography>
      </Box>
      {gargalo && (
        <Box textAlign="right">
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Maior gargalo: {gargalo.status}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: COR_TEMPO }}>
            {formatarHoras(gargalo.tempoMedioHoras)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function MonitorOsPrazosGargalos({ filtrosBody }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    buscarPrazosGargalos();
  }, [filtrosBody]);

  async function buscarPrazosGargalos() {
    setLoading(true);
    const result =
      await ordemServicoService.getRelatorioDashboardPrazosGargalos(
        filtrosBody
      );
    if (!result.isAxiosError) {
      setRows(result.data || []);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const sortedRows = [...rows].sort((a, b) => b.atrasadas - a.atrasadas);
  const atrasadas = sortedRows.map((r) => ({ ...r, valor: r.atrasadas }));
  const tempos = sortedRows.map((r) => ({ ...r, valor: r.tempoMedioHoras }));
  const maxAtraso = Math.max(...atrasadas.map((r) => r.valor), 0);
  const maxTempo = Math.max(...tempos.map((r) => r.valor), 0);
  const totalAtrasadas = rows.reduce((acc, r) => acc + (r.atrasadas || 0), 0);
  const gargalo = tempos.find((r) => r.valor === maxTempo && maxTempo > 0);

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: "1px solid #f0f0f0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 2 },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Prazos e Gargalos
          </Typography>
          <i
            className="ph-bold ph-gauge"
            style={{ color: "#64748b", fontSize: 20 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={32}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={{ flex: 1 }}
          >
            <i
              className="ph-bold ph-check-circle"
              style={{ fontSize: 32, color: "#388e3c" }}
            />
            <Typography variant="body2" color="text.secondary">
              Nenhuma O.S em etapas personalizadas no período selecionado.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                <ListaBarras
                  titulo="OS Atrasadas por Status"
                  itens={atrasadas}
                  cor={COR_ATRASO}
                  max={maxAtraso}
                  formatarValor={(item) => item.atrasadas}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                <ListaBarras
                  titulo="Tempo Médio por Status"
                  itens={tempos}
                  cor={COR_TEMPO}
                  max={maxTempo}
                  formatarValor={(item) => formatarHoras(item.tempoMedioHoras)}
                  renderSub={(item) =>
                    item.tempoPrevistoHoras != null ? (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.disabled", display: "block" }}
                      >
                        meta {formatarHoras(item.tempoPrevistoHoras)}
                      </Typography>
                    ) : null
                  }
                />
              </Grid>
            </Grid>
            <ResumoTotais totalAtrasadas={totalAtrasadas} gargalo={gargalo} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorOsPrazosGargalos;
